// Plan Rating: separates plan quality from disaster luck. The game
// is deterministic given (build plan, retire plan, seed), so the
// player's exact plan is re-resolved across many alternate seeds —
// fresh disaster draws, output rolls, and COP 31 outcome — to give
// the plan's expected cost and place the realized run within that
// distribution. Pure simulation; no game rules change.

import { createRng, deriveSeed } from './rng.js';
import { resolveGame, rollWorldEvents } from './game.js';
import { resolveAdvancedGame } from './advancedGame.js';
import { PLANT_BY_ID } from '../data/plants.js';
import { DISASTERS } from '../data/disasters.js';
import { ADVANCED_PLANT_BY_ID } from '../data/advancedCapacity.js';
import { ADVANCED_DISASTER_POOL } from '../data/advancedDisasters.js';
import { GAME_MODE } from '../data/modes.js';

// The alternate histories are drawn from a FIXED seed, never from the
// player's own.
//
// Deriving them from the player's seed made the rating a lottery. Two
// players submitting an identical plan got different expectedCost
// values - SD $1.71M in Classic and $2.02M in Advanced, with a spread
// across players of $9.78M and $12.10M - against a Best Plans board
// only $8-10M wide from top to bottom. The noise was the size of the
// entire competitive field.
//
// Worse, it was farmable. Every completed game auto-submits and the
// board keeps a player's best entry, so replaying one unchanged plan
// is a free re-draw from that distribution. Measured over 400 players:
// 20 replays of the same plan bought $3.02M of apparent skill in
// Classic and $3.83M in Advanced, 50 replays $3.98M and $4.56M. With a
// fixed seed it buys exactly nothing, because the rating cannot move.
//
// Fixing the seed also makes the board a PAIRED comparison: every plan
// is judged against the identical set of histories, which is the same
// variance-reduction the calibration harness relies on.
//
// The value is arbitrary but must never change. Changing it re-ranks
// every stored entry, so it is versioned below and a change means a
// version bump and a back-fill.
export const PLAN_RATING_SEED = 20260801;

// Raised from 150 alongside the fixed seed. The fixed seed removes the
// spread BETWEEN players; the history count is what decides how close
// the shared estimate lands to the plan's true expected cost, and it
// is the only lever on ranking accuracy once the seed is pinned.
//
// Measured over 40 real frontier plans against a 20,000-history truth,
// averaged over 12 choices of constant - mean places a plan moves from
// its true rank:
//
//   today (a seed per player), 150 histories   2.40
//   fixed seed,  150 histories                 2.17
//   fixed seed,  300 histories                 1.78
//   fixed seed,  600 histories                 1.25
//   fixed seed, 1200 histories                 1.02
//
// 600 is where the curve flattens against its cost: one rating takes
// about 13ms against 3ms at 150, and it runs once, deferred, on the
// final score screen. 1200 buys a further 0.23 of a place for twice
// the work.
export const PLAN_RATING_HISTORIES = 600;

// Bumped whenever PLAN_RATING_SEED or PLAN_RATING_HISTORIES changes,
// because either makes a stored expectedCost incomparable with a fresh
// one. The leaderboard ranks Best Plans on this version only, so a
// stale entry drops off that board rather than silently out-ranking
// entries measured a different way. Same treatment pre-Plan-Rating
// entries already get.
export const PLAN_RATING_VERSION = 2;

// shortLabel drops the "run" noun for table cells, where the column
// header already supplies that context; label is the sentence form
// used in the final-score prose.
// `article` exists so bands can be read into a sentence ("This is
// an Unlucky run") without the caller guessing at a/an.
export const LUCK_BANDS = [
  { min: 0.8, id: 'very-lucky', label: 'Very lucky run', shortLabel: 'Very lucky', article: 'a' },
  { min: 0.6, id: 'lucky', label: 'Lucky run', shortLabel: 'Lucky', article: 'a' },
  { min: 0.4, id: 'typical', label: 'Typical run', shortLabel: 'Typical', article: 'a' },
  { min: 0.2, id: 'unlucky', label: 'Unlucky run', shortLabel: 'Unlucky', article: 'an' },
  { min: 0, id: 'very-unlucky', label: 'Very unlucky run', shortLabel: 'Very unlucky', article: 'a' }
];

export function classifyLuck(luckPercentile) {
  return LUCK_BANDS.find((band) => luckPercentile >= band.min) ?? LUCK_BANDS[
    LUCK_BANDS.length - 1
  ];
}

export function simulatePlanCosts({
  mode,
  buildPlan,
  retirePlan = {},
  histories = PLAN_RATING_HISTORIES,
  // Overridable only so tests and the noise measurements can vary it.
  // Production must never pass this.
  ratingSeed = PLAN_RATING_SEED,
  ...rest
}) {
  // A caller still passing the player's seed would be silently ignored
  // and would look like it was working, which is the exact bug this
  // change fixes. Fail loudly instead.
  if ('seed' in rest) {
    throw new Error(
      'simulatePlanCosts no longer takes the player seed: the alternate ' +
        'histories come from PLAN_RATING_SEED so that identical plans rate ' +
        'identically. Pass ratingSeed only if you are deliberately varying it.'
    );
  }
  const advanced = mode === GAME_MODE.ADVANCED_CAPACITY;
  const costs = [];
  for (let i = 0; i < histories; i++) {
    const matchSeed = deriveSeed(ratingSeed >>> 0, `plan-rating:${i}`);
    const worldEvents = rollWorldEvents(
      createRng(deriveSeed(matchSeed, 'world'))
    );
    const planRng = createRng(deriveSeed(matchSeed, 'plan'));
    const game = advanced
      ? resolveAdvancedGame({
          buildPlan,
          retirePlan,
          worldEvents,
          archetypeRng: planRng,
          plantsById: ADVANCED_PLANT_BY_ID,
          disasterDeck: ADVANCED_DISASTER_POOL
        })
      : resolveGame({
          buildPlan,
          worldEvents,
          archetypeRng: planRng,
          plantsById: PLANT_BY_ID,
          disasterDeck: DISASTERS
        });
    costs.push(game.totalCost);
  }
  return costs;
}

// luckPercentile is the share of alternate histories that cost MORE
// than the realized run: 0.84 reads "luckier than 84% of histories".
export function computePlanRating({
  mode,
  buildPlan,
  retirePlan = {},
  realizedCost,
  histories = PLAN_RATING_HISTORIES,
  ratingSeed = PLAN_RATING_SEED,
  ...rest
}) {
  const costs = simulatePlanCosts({
    mode,
    buildPlan,
    retirePlan,
    histories,
    ratingSeed,
    ...rest
  });
  const sorted = [...costs].sort((a, b) => a - b);
  const expectedCost = costs.reduce((s, c) => s + c, 0) / costs.length;
  const at = (q) => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))];
  const luckPercentile =
    costs.filter((c) => c > realizedCost).length / costs.length;
  return {
    histories: costs.length,
    // Travels with the rating so a submission records how it was
    // measured, rather than the server having to assume.
    version: PLAN_RATING_VERSION,
    expectedCost,
    bestCost: sorted[0],
    worstCost: sorted[sorted.length - 1],
    p10Cost: at(0.1),
    p90Cost: at(0.9),
    luckPercentile,
    luckBand: classifyLuck(luckPercentile)
  };
}
